import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import AdvancedMarker from '../common/AdvancedMarker';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as AvailableIcon,
  DoNotDisturb as BusyIcon,
  Cancel as OfflineIcon,
  Person as DriversIcon,
  Route as RidesIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDrivers } from '../../redux/slices/driversSlice';
import LocationService from '../../utils/locationService';

// Standardoptionen für die Google Maps-Karte
const mapContainerStyle = {
  width: '100vw',
  height: '700px', // Höhere Karte für bessere Sichtbarkeit
  margin: 0,
  padding: 0
};

const defaultCenter = {
  lat: 50.1109, // Frankfurt am Main, Deutschland
  lng: 8.6821
};

const defaultOptions = {
  fullscreenControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  zoomControl: true,
  minZoom: 3,
  maxZoom: 18,
  mapTypeControlOptions: {
    position: 2, // TOP_LEFT
    style: 1 // HORIZONTAL_BAR
  },
  streetViewControlOptions: {
    position: 3 // TOP_RIGHT
  },
  fullscreenControlOptions: {
    position: 7 // LEFT_BOTTOM
  },
  // Map ID für Advanced Markers (benötigt für AdvancedMarkerElement)
  mapId: process.env.REACT_APP_GOOGLE_MAPS_ID || '8bb9c199f544a76'
};

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let icon;
  
  switch (status) {
    case 'available':
      color = 'success';
      icon = <AvailableIcon />;
      break;
    case 'busy':
      color = 'primary';
      icon = <BusyIcon />;
      break;
    case 'offline':
    default:
      color = 'default';
      icon = <OfflineIcon />;
      break;
  }
  
  return {
    color: status === 'offline' ? 'inherit' : 'white',
    backgroundColor: status === 'offline' ? undefined : theme.palette[color].main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const LiveMap = React.memo(() => {
  console.log("LiveMap: Komponente wird gerendert");
  const dispatch = useDispatch();
  const { drivers, isLoading, error } = useSelector(state => state.drivers);
  
  const [map, setMap] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'available', 'busy', 'offline'
  const [driverLocations, setDriverLocations] = useState({});
  
  // Referenz für den Unsubscribe-Callback
  const unsubscribeRef = useRef(null);
  
  // Referenz für die letzten Parameter
  const lastParamsRef = useRef(null);
  
  // Google Maps API laden - Verwendung der globalen Definition aus index.js
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    // Verwende die globale Definition, um Referenzgleichheit sicherzustellen
    libraries: window.GOOGLE_MAPS_LIBRARIES,
    version: 'weekly' // Sicherstellen, dass die neueste Version geladen wird
  });
  
  // Flag, um zu verhindern, dass die Daten mehrmals geladen werden
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Debounce-Flag und Zeitstempel für Listener-Änderungen
  const lastListenerSetupRef = useRef(0);
  const debounceTimeMs = 2000; // 2 Sekunden Mindestabstand zwischen Listener-Setups
  
  // Referenz, um parallele Initialisierungen zu verhindern
  const initializingRef = useRef(false);
  
  // Fahrer einmalig laden - Ein einmaliges Flag, das außerhalb des useEffect lebt
  const isInitialLoadRef = useRef(true);
  
  useEffect(() => {
    // Hier vermeiden wir wiederholte Ausführungen mit einem einmaligen Flag
    if (!isInitialLoadRef.current) return;
    isInitialLoadRef.current = false;
    
    const loadDrivers = async () => {
      // Prüfen ob bereits geladen oder bereits in Initialisierung
      if (dataLoaded || initializingRef.current) return;
      
      // Flag setzen, dass wir gerade initialisieren
      initializingRef.current = true;
      
      console.log("LiveMap: Initialisiere Fahrerdaten (einmalig)...");
      
      try {
        // Überprüfen, ob Fahrer bereits im Store sind
        if (!drivers || drivers.length === 0) {
          await dispatch(fetchDrivers({ 
            forceRefresh: false,
            simplified: false // Wir brauchen die Standorte für die Karte
          }));
        }
        
        console.log("LiveMap: Initialisierung erfolgreich abgeschlossen");
        setDataLoaded(true);
      } catch (error) {
        console.error("LiveMap: Fehler bei der Initialisierung:", error);
        setDataLoaded(false);
        // Bei Fehler das einmalige Flag zurücksetzen, damit noch ein Versuch möglich ist
        isInitialLoadRef.current = true;
      } finally {
        // Flag zurücksetzen mit einer kurzen Verzögerung
        setTimeout(() => {
          initializingRef.current = false;
        }, 1000);
      }
    };
    
    loadDrivers();
    
    // Cleanup-Funktion
    return () => {
      // Falls die Komponente unmounted wird während die Initialisierung läuft
      if (initializingRef.current) {
        console.log("LiveMap: Initialisierung abgebrochen (Komponente unmounted)");
        initializingRef.current = false;
      }
    };
  }, [dispatch, drivers.length]); // Nur auf die Länge der Fahrer-Liste achten // dataLoaded aus den Abhängigkeiten entfernt, um Schleifen zu vermeiden
  
  // Cleanup-Funktion für Komponenten-Unmount
  useEffect(() => {
    console.log("LiveMap: Setup Komponenten-Unmount Cleanup");
    
    return () => {
      // Beim Unmount der Komponente sicherstellen, dass alle Listener aufgeräumt werden
      if (typeof unsubscribeRef.current === 'function') {
        try {
          unsubscribeRef.current();
          console.log("LiveMap: Komponente unmounted, Listener aufgeräumt");
        } catch (e) {
          console.error("LiveMap: Fehler beim Aufräumen des Listeners:", e);
        }
        unsubscribeRef.current = null;
      }
    };
  }, []);
  
  // Echtzeit-Updates und Listener verwalten - optimiert für weniger Neuinitialisierungen
  useEffect(() => {
    // Nur ausführen, wenn dataLoaded true ist und die Komponente nicht in Initialisierung ist
    if (!dataLoaded || initializingRef.current) return;
    
    // Flag für diesen Durchlauf
    const setupRunId = Date.now();
    
    // Debounce-Logik - Nur ausführen, wenn ausreichend Zeit vergangen ist
    if (setupRunId - lastListenerSetupRef.current < debounceTimeMs) {
      console.log("LiveMap: Listener-Setup übersprungen (Debounce aktiv)");
      return;
    }
    
    console.log("LiveMap: Prüfe Listener-Setup für", { filterStatus });
    
    // Funktionen für verschiedene Listener-Typen
    const setupAllDriversListener = () => {
      console.log("LiveMap: Setze Listener für alle Fahrer");
      return LocationService.subscribeToAllDriverLocations((locations) => {
        setDriverLocations(locations);
      });
    };
    
    const setupFilteredDriversListener = (status) => {
      console.log(`LiveMap: Setze Listener für Fahrer mit Status '${status}'`);
      return LocationService.subscribeToDriversByStatus(status, (locations) => {
        setDriverLocations(locations);
      });
    };
    
    // Prüfen, ob Setup wirklich nötig ist (wenn Parameter geändert wurden)
    let shouldSetupNewListener = true;
    
    if (typeof unsubscribeRef.current === 'function' && lastParamsRef.current) {
      const sameParams = lastParamsRef.current.filterStatus === filterStatus;
      
      if (sameParams) {
        console.log("LiveMap: Parameter unverändert, behalte vorhandenen Listener");
        shouldSetupNewListener = false;
      }
    }
    
    // Nur einen neuen Listener einrichten, wenn nötig
    if (shouldSetupNewListener) {
      console.log("LiveMap: Richte neuen Listener ein");
      
      // Zeitstempel aktualisieren
      lastListenerSetupRef.current = setupRunId;
      
      // Bestehenden Listener aufräumen
      if (typeof unsubscribeRef.current === 'function') {
        try {
          unsubscribeRef.current();
          console.log("LiveMap: Alter Listener erfolgreich entfernt");
        } catch (e) {
          console.error("LiveMap: Fehler beim Entfernen des alten Listeners:", e);
        }
        unsubscribeRef.current = null;
      }
      
      // Neuen Listener einrichten
      try {
        let newCleanup = filterStatus === 'all' 
          ? setupAllDriversListener()
          : setupFilteredDriversListener(filterStatus);
        
        // Aktuelle Parameter speichern
        lastParamsRef.current = {
          filterStatus,
          timestamp: setupRunId
        };
        
        // Cleanup-Funktion speichern
        unsubscribeRef.current = newCleanup;
        console.log("LiveMap: Neuer Listener erfolgreich eingerichtet");
      } catch (error) {
        console.error("LiveMap: Fehler beim Einrichten des Listeners:", error);
      }
    }
    
    // Cleanup-Funktion
    return () => {
      // Nur aufräumen, wenn wir in diesem Durchlauf einen neuen Listener eingerichtet haben
      if (lastParamsRef.current && lastParamsRef.current.timestamp === setupRunId) {
        console.log("LiveMap: Cleanup im selben Durchlauf, in dem Setup durchgeführt wurde");
      }
      
      if (typeof unsubscribeRef.current === 'function') {
        try {
          console.log("LiveMap: useEffect Cleanup - Listener aufräumen");
          unsubscribeRef.current();
          
          // Referenz nur löschen, wenn dieser Cleanup zu demselben Durchlauf gehört
          if (lastParamsRef.current && lastParamsRef.current.timestamp === setupRunId) {
            unsubscribeRef.current = null;
          }
        } catch (e) {
          console.error("LiveMap: Fehler beim Aufräumen des Listeners:", e);
        }
      }
    };
  }, [dataLoaded, filterStatus]);
  
  // Karte initialisieren
  const onMapLoad = useCallback((map) => {
    setMap(map);
  }, []);
  
  // Kartengrenzen anpassen, um alle aktiven Fahrer anzuzeigen
  const fitBounds = useCallback(() => {
    if (!map || !drivers || drivers.length === 0) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    let hasBounds = false;
    
    const activeDrivers = drivers.filter(driver => 
      (filterStatus === 'all' || driver.status === filterStatus) && 
      driver.location && 
      driver.location.latitude && 
      driver.location.longitude
    );
    
    activeDrivers.forEach(driver => {
      bounds.extend({
        lat: driver.location.latitude,
        lng: driver.location.longitude
      });
      hasBounds = true;
    });
    
    if (hasBounds) {
      map.fitBounds(bounds);
      
      // Zoom-Level anpassen, wenn nur ein Fahrer vorhanden ist
      if (activeDrivers.length === 1) {
        map.setZoom(15);
      }
    }
  }, [map, drivers, filterStatus]);
  
  // Grenzen anpassen, wenn sich die Fahrerdaten oder Filter ändern
  useEffect(() => {
    if (map) {
      fitBounds();
    }
  }, [map, drivers, filterStatus, fitBounds]);
  
  // Fahrer nach Status filtern
  const handleFilterChange = (status) => {
    setFilterStatus(status);
  };
  
  // Karte aktualisieren
  const handleRefresh = () => {
    dispatch(fetchDrivers());
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'available': 'Verfügbar',
      'busy': 'Beschäftigt',
      'offline': 'Offline'
    };
    
    return statusMap[status] || status;
  };
  
  // Marker-Icons für verschiedene Fahrerstatus
  // Als konstantes Objekt definiert, um unnötige Re-Renderings zu vermeiden
  const MARKER_ICONS = {
    available: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
    busy: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
    offline: 'https://maps.google.com/mapfiles/ms/icons/gray-dot.png'
  };
  
  // Konstante Größe für alle Marker
  const markerSize = useMemo(() => ({
    width: 32,
    height: 32
  }), []);
  
  // Marker-Icon basierend auf Fahrerstatus
  const getMarkerIcon = useCallback((status) => {
    return MARKER_ICONS[status] || MARKER_ICONS.offline;
  }, [MARKER_ICONS]);
  
  // Lade-Fehler anzeigen
  if (loadError) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Google Maps API: {loadError.message}
      </Alert>
    );
  }
  
  // Fahrer-Ladevorgang anzeigen
  if (isLoading && !drivers.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Fahrerdaten werden geladen...
        </Typography>
      </Box>
    );
  }
  
  // Google Maps Ladevorgang anzeigen
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Karte wird geladen...
        </Typography>
      </Box>
    );
  }
  
  // Fehler bei der Fahrerladung anzeigen
  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={() => dispatch(fetchDrivers())}>
            Erneut versuchen
          </Button>
        }
      >
        Fehler beim Laden der Fahrerdaten: {error}
      </Alert>
    );
  }
  
  // Gefilterte Fahrer - konsistente Statusprüfung
  const filteredDrivers = drivers.filter(driver => {
    // Konsistente Statusverarbeitung
    const driverStatus = driver.status || 
                        (driver.isAvailable ? 'available' : 'offline');
    
    return filterStatus === 'all' || driverStatus === filterStatus;
  });
  
  return (
    <Card elevation={3} className="dashboard-live-map" sx={{ borderRadius: 0, width: '100%', maxWidth: '100%' }}>
      <CardContent sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Live-Fahrerstandorte
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={handleRefresh} 
              disabled={isLoading} 
              title="Aktualisieren"
              sx={{ 
                bgcolor: 'primary.light', 
                '&:hover': { bgcolor: 'primary.main', color: 'white' } 
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Statusfilter */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', px: 2 }}>
          <Chip 
            label="Alle" 
            variant={filterStatus === 'all' ? 'filled' : 'outlined'}
            color={filterStatus === 'all' ? 'primary' : 'default'}
            onClick={() => handleFilterChange('all')}
          />
          <StatusChip 
            label="Verfügbar" 
            status="available"
            variant={filterStatus === 'available' ? 'filled' : 'outlined'}
            onClick={() => handleFilterChange('available')}
          />
          <StatusChip 
            label="Beschäftigt" 
            status="busy"
            variant={filterStatus === 'busy' ? 'filled' : 'outlined'}
            onClick={() => handleFilterChange('busy')}
          />
          <StatusChip 
            label="Offline" 
            status="offline"
            variant={filterStatus === 'offline' ? 'filled' : 'outlined'}
            onClick={() => handleFilterChange('offline')}
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
            {filteredDrivers.length} Fahrer angezeigt
          </Typography>
        </Box>
        
        {/* Karte */}
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={12}
          options={defaultOptions}
          onLoad={onMapLoad}
          mapContainerClassName="google-map-container"
        >
          {/* Fahrer-Marker - mit Echtzeit-Updates */}
          {filteredDrivers.map(driver => {
            // Prüfen, ob für diesen Fahrer Echtzeit-Standortdaten vorliegen
            const realtimeLocation = driverLocations[driver.id];
            
            // Wenn Echtzeit-Standortdaten vorhanden sind, diese verwenden
            // Andernfalls die regulären Standortdaten aus dem Driver-Objekt verwenden
            const hasLocation = realtimeLocation || 
                               (driver.location && 
                                driver.location.latitude && 
                                driver.location.longitude);
            
            // Keine Anzeige, wenn keine Standortdaten verfügbar sind
            if (!hasLocation) return null;
            
            // Position basierend auf Echtzeit oder gespeicherten Daten
            const position = realtimeLocation 
              ? {
                  lat: realtimeLocation.latitude,
                  lng: realtimeLocation.longitude
                }
              : {
                  lat: driver.location.latitude,
                  lng: driver.location.longitude
                };
            
            // Animation nur für Echtzeit-Updates aktivieren
            const animation = realtimeLocation ? 
                              window.google.maps.Animation.BOUNCE : 
                              null;
            
            // Fahrer mit Echtzeit-Updates haben einen speziellen Titel
            const title = realtimeLocation
              ? `${driver.user?.name || 'Fahrer'} (Live)`
              : driver.user?.name || 'Unbekannter Fahrer';
            
            return (
              <AdvancedMarker
                key={driver.id}
                position={position}
                icon={{
                  url: getMarkerIcon(driver.status),
                  scaledSize: new window.google.maps.Size(markerSize.width, markerSize.height)
                }}
                animation={animation}
                onClick={() => setSelectedDriver(driver)}
                title={title}
              />
            );
          })}
          
          {/* Info-Fenster für ausgewählten Fahrer */}
          {selectedDriver && (
            selectedDriver.location && (
              <InfoWindow
                position={{
                  lat: selectedDriver.location.latitude,
                  lng: selectedDriver.location.longitude
                }}
                onCloseClick={() => setSelectedDriver(null)}
              >
                <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                  <Typography variant="subtitle1">
                    {selectedDriver.user?.name || 'Unbekannter Fahrer'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                    <StatusChip 
                      label={translateStatus(selectedDriver.status)} 
                      status={selectedDriver.status}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2">
                    Telefon: {selectedDriver.user?.phone || 'Nicht angegeben'}
                  </Typography>
                  <Typography variant="body2">
                    Bewertung: {selectedDriver.rating || 'Keine'} ({selectedDriver.total_rides || 0} Fahrten)
                  </Typography>
                  <Typography variant="body2">
                    Fahrzeug: {selectedDriver.vehicle ? 
                      `${selectedDriver.vehicle.model} (${selectedDriver.vehicle.license_plate})` : 
                      'Kein Fahrzeug zugewiesen'}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    component={Link} 
                    to={`/fahrer/${selectedDriver.id}`}
                    sx={{ mt: 1 }}
                    fullWidth
                  >
                    Profil anzeigen
                  </Button>
                </Box>
              </InfoWindow>
            )
          )}
        </GoogleMap>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          component={Link} 
          to="/fahrer"
          size="medium"
          variant="outlined"
          startIcon={<DriversIcon />}
          sx={{ fontWeight: 'bold' }}
        >
          Alle Fahrer anzeigen
        </Button>
        <Button 
          component={Link} 
          to="/fahrten"
          size="medium"
          variant="contained"
          startIcon={<RidesIcon />}
          sx={{ fontWeight: 'bold' }}
        >
          Aktive Fahrten anzeigen
        </Button>
      </CardActions>
    </Card>
  );
});

export default LiveMap;